import React, { useState } from 'react';
import styled from '@emotion/styled/macro';
import { keyframes } from '@emotion/react';
import { flipInX } from 'react-animations';
import Translate from '@utils/translation-functions';
import vars from '@variables';
import { FlexibleLinkAtom } from '@atoms/link-atoms/link-atoms';
import { getImageParameters } from '../../mixins';
import { GTM_ID } from '../../../../common/constants/gtmIds';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { LogoCardProps } from './logo-card.types';
import { PAtom } from '@atoms/text-atoms/text-atoms';
import { FlipIcon } from '../flip-card/flip-card.styles';

const LogoCard: React.FC<LogoCardProps> = ({
  name,
  type,
  url,
  linkText,
  gatsbyImageData,
  logoSrc,
  logoFormat,
  logoAlt,
  className,
  translate = true,
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const gatsbyImage = getImage(gatsbyImageData);

  if (!logoSrc || !name) return null;

  const handleFlip = () => setIsFlipped(prev => !prev);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleFlip();
    }
  };

  return (
    <div
      className={`${className} ${isFlipped ? 'flipped' : ''}`}
      data-testid="logo-card"
    >
      <div className="front" data-testid="logo-card-front">
        {gatsbyImage &&
        logoFormat !== 'image/gif' &&
        logoFormat !== 'image/svg+xml' ? (
          <GatsbyImage
            image={gatsbyImage}
            alt={logoAlt || `${name} logo`}
            className="gatsby-image-pos"
          />
        ) : (
          <div className="gatsby-image-wrapper gatsby-image-wrapper-constrained gatsby-image-pos">
            <img
              src={getImageParameters(
                logoSrc,
                vars.defaultImageFormat,
                300,
                300,
                'pad'
              )}
              alt={logoAlt || `${name} logo`}
            />
          </div>
        )}

        {/* Mobile/touch flip button */}
        {url && (
          <div
            role="button"
            tabIndex={0}
            className="flip-button"
            onClick={handleFlip}
            onKeyDown={handleKeyDown}
            aria-label={`Show more about ${name}`}
          >
            <FlipIcon aria-label="Flip card for more information">
              <img
                src="https://uploads-ssl.webflow.com/665871d25ce3f664ba8a7e07/66a8ad049347e24fc512737c_Group%20574.svg"
                alt="Flip card"
              />
              <span>{Translate('Flip', translate)}</span>
            </FlipIcon>
          </div>
        )}
      </div>

      <div className="back">
        <FlexibleLinkAtom url={url} gtmId={GTM_ID.LOGO_BLOCK}>
          {name && <PAtom className="name">{name}</PAtom>}
          {type && <PAtom className="type">{type}</PAtom>}
          {url && (
            <PAtom className="link-text">
              {linkText || Translate('Learn more', translate)}
            </PAtom>
          )}
        </FlexibleLinkAtom>
      </div>
    </div>
  );
};

const StyledLogoCard = styled(LogoCard)`
  width: 100%;

  .front {
    display: block;
    overflow: hidden;
    .gatsby-image-wrapper-constrained {
      display: inline-block;
      top: 50%;
      transform: translateY(-50%);
      position: absolute;
      vertical-align: unset;
    }
  }

  .back {
    display: none;
    ${vars.fontSize1};
    padding: ${vars.spacingXs} ${vars.spacingM};

    .name {
      padding: 0;
    }

    .type {
      font-weight: ${vars.fontWeightBold};
      padding-top: 0;
      margin-top: 0;
    }

    ${FlexibleLinkAtom} {
      text-decoration: none;
    }
  }

  .flip-button {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 3;
    width: 100%;
    height: 100%;
    background: transparent;
    border: none;
    cursor: pointer;
    display: none;

    ${FlipIcon} {
      img {
        width: 50%;
        max-height: 50%;
      }
    }

    p {
      font-size: 1.4rem;
      padding: 2px 6px;
    }
  }

  &.flipped {
    .front {
      display: none !important;
    }

    .back {
      display: flex !important;
      animation: 0.5s ${keyframes`${flipInX}`} 1;
    }
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      .front {
        display: none !important;
      }
      .back {
        display: flex !important;
        animation: 0.5s ${keyframes`${flipInX}`} 1;
      }
    }
  }

  @media (hover: none) {
    .flip-button {
      display: flex;
      align-items: flex-end;
      justify-content: flex-end;
      padding-bottom: 5px;
    }
  }

  width: ${vars.logoCardSize};
  height: ${vars.logoCardSize};
  position: relative;
  overflow: hidden;

  .front,
  .back {
    position: absolute;
    border: 1px solid ${vars.borderGrey};
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .front {
    z-index: 2;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;

    img {
      width: 100%;
      max-height: 100%;
      object-fit: cover;
    }
  }

  .back {
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 0 ${vars.spacingS};

    ${FlexibleLinkAtom} {
      border: ${vars.logoCardBackBorder} solid ${vars.transparentBorderGrey};
      display: flex;
      flex-direction: column;
      position: absolute;
      width: 100%;
      height: 100%;
      cursor: ${({ url }) => (url ? 'pointer' : 'auto')};
      text-decoration: none;
      align-items: center;
      justify-content: center;
      background: white;

      ${PAtom} {
        color: ${vars.coreDarkGrey};
        background: transparent !important;
        ${vars.fontSize2};
        padding: 0 ${vars.spacingS};

        .name {
          margin: 0 ${vars.spacingS};
          color: black;
          padding-bottom: ${({ linkText }) =>
            linkText ? vars.spacingL : null};
        }

        .type {
          font-weight: ${vars.fontWeightBold};
          color: ${vars.fadedTextGrey};
          padding-bottom: ${({ linkText }) =>
            linkText ? vars.spacingL : null};
        }
      }

      .link-text {
        display: block;
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        padding: ${vars.spacingS} 0;
        background: ${vars.transparentBorderGrey} !important;
        font-weight: ${vars.fontWeightBold};
        ${vars.fontSize2};
        color: black;
        padding-bottom: 0;
        margin-bottom: 0;
      }
    }
  }
`;

export default StyledLogoCard;
